import type { Round } from "../api/types";
import { formatDuration, roundClock } from "../lib/view";
import { SPEEDS, useCoarseTime, usePlayback } from "../player/store";

interface Props {
  round: Round;
  onPreviousRound: () => void;
  onNextRound: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
}

/** Transport controls: round navigation, play/pause, speed and the clock. */
export function Controls({ round, onPreviousRound, onNextRound, hasPrevious, hasNext }: Props) {
  const playing = usePlayback((s) => s.playing);
  const speed = usePlayback((s) => s.speed);
  const toggle = usePlayback((s) => s.toggle);
  const setSpeed = usePlayback((s) => s.setSpeed);
  const timeMs = useCoarseTime(100);

  const elapsed = Math.max(0, timeMs - round.startMs);
  const total = Math.max(1, round.endMs - round.startMs);

  return (
    <div className="flex items-center gap-3 px-3 py-2">
      <button
        onClick={onPreviousRound}
        disabled={!hasPrevious}
        title="Round précédent"
        className="rounded px-2 py-1 text-sm disabled:opacity-30 hover:bg-white/10"
      >
        ⏮
      </button>
      <button
        onClick={toggle}
        className="rounded bg-white/10 px-4 py-1.5 text-sm font-medium hover:bg-white/20"
      >
        {playing ? "⏸ Pause" : "▶ Lecture"}
      </button>
      <button
        onClick={onNextRound}
        disabled={!hasNext}
        title="Round suivant"
        className="rounded px-2 py-1 text-sm disabled:opacity-30 hover:bg-white/10"
      >
        ⏭
      </button>

      <div className="ml-2 flex items-center gap-1">
        {SPEEDS.map((value) => (
          <button
            key={value}
            onClick={() => setSpeed(value)}
            className={`rounded px-2 py-1 text-xs ${
              speed === value ? "bg-white/25 font-semibold" : "hover:bg-white/10 opacity-70"
            }`}
          >
            {value}×
          </button>
        ))}
      </div>

      <div className="ml-auto flex items-baseline gap-3 font-mono text-sm">
        <span className="opacity-60">{formatDuration(elapsed)} / {formatDuration(total)}</span>
        <span className="text-base" title="Chronomètre du round">
          {roundClock(round, timeMs)}
        </span>
      </div>
    </div>
  );
}
